import { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { inr } from "@/lib/format";

export default function TenantOffers() {
  return (
    <div className="p-8">
      <h1 className="font-display text-3xl font-bold tracking-tight mb-1" data-testid="tenant-offers-title">Offers & Coupons</h1>
      <p className="text-slate-500 text-sm mb-6">Drive bookings with featured offers and discount codes.</p>
      <Tabs defaultValue="offers">
        <TabsList>
          <TabsTrigger value="offers" data-testid="tab-offers">Offers</TabsTrigger>
          <TabsTrigger value="coupons" data-testid="tab-coupons">Coupons</TabsTrigger>
        </TabsList>
        <TabsContent value="offers"><OffersTab /></TabsContent>
        <TabsContent value="coupons"><CouponsTab /></TabsContent>
      </Tabs>
    </div>
  );
}

function OffersTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", description: "", image: "", discount_type: "percent", discount_value: 10, applies_to: "all", is_active: true });
  const refresh = () => api.get("/tenant/offers").then(({ data }) => setItems(data));
  useEffect(() => { refresh(); }, []);
  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/tenant/offers", form);
      setForm({ title: "", description: "", image: "", discount_type: "percent", discount_value: 10, applies_to: "all", is_active: true });
      refresh();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (id) => { if (confirm("Delete?")) { await api.delete(`/tenant/offers/${id}`); refresh(); } };
  return (
    <Card className="p-5 mt-4 border-slate-200">
      <form onSubmit={add} className="grid md:grid-cols-2 gap-3 mb-5">
        <Input placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="offer-title-input" />
        <Input placeholder="Image URL" value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} />
        <Input placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        <Select value={form.discount_type} onValueChange={(v) => setForm({ ...form, discount_type: v })}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="percent">% Discount</SelectItem><SelectItem value="flat">₹ Flat</SelectItem></SelectContent>
        </Select>
        <Input placeholder="Discount value" type="number" value={form.discount_value} onChange={(e) => setForm({ ...form, discount_value: +e.target.value })} data-testid="offer-value-input" />
        <Button type="submit" data-testid="offer-add-btn"><Plus className="h-4 w-4 mr-2" />Add Offer</Button>
      </form>
      <div className="space-y-2">
        {items.map((o) => (
          <div key={o.id} className="flex justify-between items-center border border-slate-200 rounded-md p-3" data-testid={`offer-row-${o.id}`}>
            <div className="flex gap-3 items-center">
              {o.image && <img src={o.image} className="w-12 h-12 object-cover rounded" alt="" />}
              <div>
                <div className="font-medium">{o.title}</div>
                <div className="text-xs text-slate-500">{o.discount_type === "percent" ? `${o.discount_value}% off` : `${inr(o.discount_value)} off`}</div>
              </div>
            </div>
            <Button variant="ghost" size="icon" onClick={() => del(o.id)}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
          </div>
        ))}
        {items.length === 0 && <p className="text-sm text-slate-400">No offers yet.</p>}
      </div>
    </Card>
  );
}

function CouponsTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ code: "", discount_type: "percent", discount_value: 10, min_order: 0, max_uses: 100, is_active: true });
  const refresh = () => api.get("/tenant/coupons").then(({ data }) => setItems(data));
  useEffect(() => { refresh(); }, []);
  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/tenant/coupons", { ...form, code: form.code.toUpperCase() });
      setForm({ code: "", discount_type: "percent", discount_value: 10, min_order: 0, max_uses: 100, is_active: true });
      refresh();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (id) => { if (confirm("Delete?")) { await api.delete(`/tenant/coupons/${id}`); refresh(); } };
  return (
    <Card className="p-5 mt-4 border-slate-200">
      <form onSubmit={add} className="grid md:grid-cols-3 gap-3 mb-5">
        <Input placeholder="CODE" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} data-testid="coupon-code-input" />
        <Select value={form.discount_type} onValueChange={(v) => setForm({ ...form, discount_type: v })}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="percent">% Discount</SelectItem><SelectItem value="flat">₹ Flat</SelectItem></SelectContent>
        </Select>
        <Input placeholder="Value" type="number" value={form.discount_value} onChange={(e) => setForm({ ...form, discount_value: +e.target.value })} />
        <Input placeholder="Min order" type="number" value={form.min_order} onChange={(e) => setForm({ ...form, min_order: +e.target.value })} />
        <Input placeholder="Max uses" type="number" value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: +e.target.value })} />
        <Button type="submit" data-testid="coupon-add-btn"><Plus className="h-4 w-4 mr-2" /> Add Coupon</Button>
      </form>
      <div className="space-y-2">
        {items.map((c) => (
          <div key={c.id} className="flex justify-between items-center border border-slate-200 rounded-md p-3" data-testid={`coupon-row-${c.id}`}>
            <div>
              <div className="font-mono font-semibold">{c.code}</div>
              <div className="text-xs text-slate-500">
                {c.discount_type === "percent" ? `${c.discount_value}% off` : `${inr(c.discount_value)} off`} • Min {inr(c.min_order)} • Used {c.used_count}/{c.max_uses}
              </div>
            </div>
            <Button variant="ghost" size="icon" onClick={() => del(c.id)}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
          </div>
        ))}
        {items.length === 0 && <p className="text-sm text-slate-400">No coupons yet.</p>}
      </div>
    </Card>
  );
}
